import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Send } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export function LoginPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [form, setForm] = useState({
    email: "",
    password: "",
    accountName: "",
    accountSlug: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "login") {
        await api.post("/v1/auth/login", {
          email: form.email,
          password: form.password,
        });
      } else {
        await api.post("/v1/auth/register", {
          email: form.email,
          password: form.password,
          accountName: form.accountName,
          accountSlug: form.accountSlug,
        });
      }
      await refresh();
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "unknown_error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-full flex flex-col items-center justify-center px-6 py-12 bg-gradient-to-br from-slate-50 to-brand-50">
      <div className="w-full max-w-md">
        {/* Logo + product */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-1">
            <div className="w-10 h-10 rounded-xl bg-brand-600 flex items-center justify-center">
              <Send size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div className="text-2xl font-semibold text-brand-600 tracking-tight">
              Mailtera Send
            </div>
          </div>
          <div className="text-xs text-mailtera-text-muted mt-1">
            Transactional & bulk email delivery
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-mailtera-border shadow-sm p-8">
          <h1 className="text-xl font-semibold text-brand-600 mb-1">
            {mode === "login" ? "Masuk" : "Buat akun"}
          </h1>
          <p className="text-sm text-mailtera-text-muted mb-6">
            {mode === "login"
              ? "Akses dashboard sending Anda."
              : "Setup account baru untuk mulai kirim email."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              placeholder="anda@perusahaan.id"
              autoFocus
              required
            />

            {/* Password with show/hide */}
            <div>
              <label className="text-sm font-medium text-mailtera-text">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  required
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  minLength={mode === "register" ? 12 : 8}
                  className="w-full h-11 pr-10 pl-3 border border-mailtera-border rounded-lg text-sm text-mailtera-text placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword ? "Sembunyikan password" : "Tampilkan password"
                  }
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {mode === "register" && (
                <div className="text-[11px] text-mailtera-text-muted mt-1">
                  Minimal 12 karakter.
                </div>
              )}
            </div>

            {mode === "register" && (
              <>
                <Field
                  label="Nama akun"
                  value={form.accountName}
                  onChange={(v) => setForm({ ...form, accountName: v })}
                  placeholder="PT Perusahaan"
                  required
                  minLength={2}
                  hint="Nama tampilan akun (min 2 karakter)"
                />
                <Field
                  label="Slug"
                  value={form.accountSlug}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      accountSlug: v.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                    })
                  }
                  placeholder="perusahaan"
                  required
                  minLength={3}
                  maxLength={32}
                  hint="3-32 karakter · huruf kecil, angka, dash · dipakai untuk URL API"
                />
              </>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy
                ? "Memproses…"
                : mode === "login"
                  ? "Masuk"
                  : "Buat akun"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="flex-1 h-px bg-slate-200" />
            atau
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError(null);
            }}
            className="w-full h-11 rounded-lg border border-mailtera-border hover:bg-slate-50 text-sm font-medium text-mailtera-text inline-flex items-center justify-center"
          >
            {mode === "login" ? "Belum punya akun? Register" : "Sudah punya akun? Masuk"}
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-mailtera-text-muted">
          Mailtera Send — Enterprise email Indonesia
        </p>
      </div>
    </main>
  );
}

function Field({
  label,
  type = "text",
  value,
  onChange,
  required,
  placeholder,
  autoFocus,
  hint,
  minLength,
  maxLength,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  hint?: string;
  minLength?: number;
  maxLength?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-mailtera-text">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        minLength={minLength}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 border border-mailtera-border rounded-lg text-sm text-mailtera-text placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      />
      {hint && (
        <div className="text-[11px] text-mailtera-text-muted mt-1">{hint}</div>
      )}
    </label>
  );
}
